import { useSelector } from 'react-redux';
import { TBusinessType } from '../../../entities/general.entities';
import { IGrowingSeed } from '../../../entities/growing.entities';
import { Dropdown, Input, Subtitle } from '../../shared';
import { selectLang } from '../../../store/ui/ui.selector';
import { selectAccount } from '../../../store/auth/auth.selector';
import { translate } from '../../../lib/lang.helper';
import {
  defaultDateFormat,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { Button } from 'antd';

const toLabel = (
  type: TBusinessType,
  seed: IGrowingSeed,
  batch: string,
  fM: string | null | undefined,
  existingLen = 0,
) =>
  type === 'MUSSEL'
    ? `${defaultDateFormat(seed.planned_date_seed)} - ${batch} ${
        seed.spat_source
      }`
    : type === 'OYSTER'
      ? `${defaultDateFormat(seed.planned_date_seed)} - ${batch} ${
          seed.spat_source
        } - ${
          seed.harvested
            ? `${Math.max(0, (seed.basket_count ?? 0) - seed.harvested + existingLen)}/`
            : ''
        }${seed.basket_count} ${fM ?? 'baskets'}`
      : '';

interface Props {
  type: TBusinessType;
  farmingMethod?: string | null;
  growingSeeds: IGrowingSeed[];
  values: number[];
  onChange: (value: number[] | undefined) => void;
  showAllOptions?: boolean;
  existingGroups?: IGroup[];
}

const HarvestBatchSelect = ({
  type,
  farmingMethod,
  growingSeeds,
  values,
  onChange,
  showAllOptions = false,
  existingGroups,
}: Props) => {
  const lang = useSelector(selectLang);

  const getExistingLen = (id: number) =>
    existingGroups?.find(g => g.batch_seed_id === id)?.line_length ?? 0;

  const isFullyHarvested = (s: IGrowingSeed) => {
    const existingLen = getExistingLen(s.id);
    if (type === 'MUSSEL') {
      return (
        s.harvested &&
        (!s.line_length || s.line_length - s.harvested + existingLen <= 0)
      );
    }
    if (type === 'OYSTER') {
      return (
        s.harvested &&
        (!s.basket_count || s.basket_count - s.harvested + existingLen <= 0)
      );
    }
    return false;
  };

  return (
    <Dropdown
      label={translate(lang, '_know_batch_harvested')}
      mode='multiple'
      placeholder={translate(lang, 'Select batch')}
      options={growingSeeds
        .filter(x => showAllOptions || !isFullyHarvested(x))
        .map(x => ({
          id: x.id.toString(),
          value: x.id.toString(),
          label: toLabel(
            type,
            x,
            translate(lang, 'Batch'),
            farmingMethod,
            getExistingLen(x.id),
          ),
        }))}
      value={values.map(String) as any}
      onChange={(v: any) => onChange(v ? v.map(Number) : undefined)}
    />
  );
};

export default HarvestBatchSelect;

interface IGroup {
  batch_seed_id: number;
  line_length?: number;
}

interface LensProps {
  type: TBusinessType;
  lineType?: string | null;
  farmingMethod?: string | null;
  growingSeeds: IGrowingSeed[];
  values: IGroup[] | undefined;
  onChange: (value: IGroup[]) => void;
  className?: string;
  existingGroups?: IGroup[];
}

export const HarvestBatchLensForm = ({
  type,
  lineType,
  farmingMethod,
  growingSeeds,
  values,
  onChange,
  className,
  existingGroups,
}: LensProps) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const unit =
    type === 'MUSSEL'
      ? lineType === 'LONGLINE'
        ? getUnitOfSpecie(unitType, 'distance_m')
        : 'droppers'
      : (farmingMethod ?? 'baskets');

  const getExistingLen = (id: number) =>
    existingGroups?.find(g => g.batch_seed_id === id)?.line_length ?? 0;

  const getMaxVal = (id: number) => {
    const seed = growingSeeds.find(x => x.id === id)!;
    const existingLen = getExistingLen(id);
    if (type === 'MUSSEL') {
      return (seed.line_length ?? 0) - (seed.harvested ?? 0) + existingLen;
    }
    if (type === 'OYSTER') {
      return (seed.basket_count ?? 0) - (seed.harvested ?? 0) + existingLen;
    }
    return 0;
  };
  const updateLength = (id: number, value: string) => {
    const line_length = value.length <= 0 ? undefined : Number(value);
    if (
      line_length !== undefined &&
      (line_length < 0 || line_length > getMaxVal(id))
    ) {
      return;
    }
    let tmp = values ? [...values] : [];
    let index = tmp.findIndex(x => x.batch_seed_id === id);
    if (index >= 0) {
      tmp[index] = { ...tmp[index], line_length };
    } else {
      tmp.push({ batch_seed_id: id, line_length });
    }
    onChange(tmp);
  };

  return (
    <div className={className}>
      <div className='mb-14'>
        <Subtitle color='black-3' align='left' size={4} fontWeight={400}>
          {translate(lang, 'Length of stripped to waste per batch')}
        </Subtitle>
      </div>
      {growingSeeds.map(seed => (
        <div key={seed.id} className='mb-12 ml-12'>
          <Input
            type='number'
            label={toLabel(
              type,
              seed,
              translate(lang, 'Batch'),
              farmingMethod,
              getExistingLen(seed.id),
            )}
            placeholder={translate(lang, 'Maximum %s', getMaxVal(seed.id))}
            value={
              values
                ?.find(x => x.batch_seed_id === seed.id)
                ?.line_length?.toString() ?? ''
            }
            max={getMaxVal(seed.id)}
            unit={unit}
            onChange={e => updateLength(seed.id, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
};

export const HarvestGroupLensForm = ({
  type,
  lineType,
  farmingMethod,
  growingSeeds,
  values,
  onChange,
  className,
  existingGroups,
}: LensProps) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const unit =
    type === 'MUSSEL'
      ? lineType === 'LONGLINE'
        ? getUnitOfSpecie(unitType, 'distance_m')
        : 'droppers'
      : (farmingMethod ?? 'baskets');
  const title =
    type === 'MUSSEL'
      ? lineType === 'LONGLINE'
        ? translate(lang, '_harvested_length_per_batch')
        : translate(lang, '_harvested_droppers_per_batch')
      : translate(lang, '_harvested_per_batch', farmingMethod ?? 'baskets');

  const getExistingLen = (id: number) =>
    existingGroups?.find(g => g.batch_seed_id === id)?.line_length ?? 0;

  const getMaxVal = (id: number) => {
    const seed = growingSeeds.find(x => x.id === id)!;
    const existingLen = getExistingLen(id);
    if (type === 'MUSSEL') {
      return (seed.line_length ?? 0) - (seed.harvested ?? 0) + existingLen;
    }
    if (type === 'OYSTER') {
      return (seed.basket_count ?? 0) - (seed.harvested ?? 0) + existingLen;
    }
    return 0;
  };
  const updateLength = (id: number, value: string) => {
    const line_length = value.length <= 0 ? undefined : Number(value);
    if (
      line_length !== undefined &&
      (line_length < 0 || line_length > getMaxVal(id))
    ) {
      return;
    }
    let tmp = values ? [...values] : [];
    let index = tmp.findIndex(x => x.batch_seed_id === id);
    if (index >= 0) {
      tmp[index] = { ...tmp[index], line_length };
    } else {
      tmp.push({ batch_seed_id: id, line_length });
    }
    onChange(tmp);
  };

  const seedGroups = growingSeeds.filter(x =>
    values?.find(v => v.batch_seed_id === x.id),
  );

  return seedGroups.length > 0 ? (
    <div className={className}>
      <div className='mb-14'>
        <Subtitle color='black-3' align='left' size={4} fontWeight={400}>
          {title}
        </Subtitle>
      </div>
      {seedGroups.map(seed => (
        <div key={seed.id} className='mb-12 ml-12'>
          <Input
            type='number'
            label={toLabel(
              type,
              seed,
              translate(lang, 'Batch'),
              farmingMethod,
              getExistingLen(seed.id),
            )}
            placeholder={translate(lang, 'Maximum %s', getMaxVal(seed.id))}
            value={
              values
                ?.find(x => x.batch_seed_id === seed.id)
                ?.line_length?.toString() ?? ''
            }
            max={getMaxVal(seed.id)}
            unit={unit}
            onChange={e => updateLength(seed.id, e.target.value)}
            rightLabelButton={
              <Button
                type='link'
                onClick={() =>
                  updateLength(seed.id, getMaxVal(seed.id).toString())
                }
              >
                {translate(lang, '_harvest_whole_batch')}
              </Button>
            }
          />
        </div>
      ))}
    </div>
  ) : (
    <></>
  );
};
